import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Heart,
  Lightbulb,
  Target,
  Handshake,
  TrendingUp,
  Quote
} from 'lucide-react';
import { aboutUs, coreValues } from '../data/mock';
import ParallaxSection from '../components/graphics/ParallaxSection';
import FloatingElements from '../components/graphics/FloatingElements';
import SEO from '../components/SEO';

const iconMap = {
  Heart,
  Lightbulb,
  Target,
  Handshake,
  TrendingUp
};

const AboutPage = () => {
  return (
    <div className="bg-slate-950 pt-20">
      <SEO 
        title="About Us"
        description="Learn about CORE IT Group - our mission, vision, and core values. We bridge the gap between technical security controls and real business risk."
        keywords="about CORE IT Group, cybersecurity company, IT security mission, security consulting firm"
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
          <FloatingElements />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">About Us</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6">
              Bridging Security & Business
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              {aboutUs.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <ParallaxSection className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Our Approach</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">
                Industry Standards, Real Experience
              </h2>
              <p className="text-slate-400 leading-relaxed">
                {aboutUs.approach}
              </p>
              
              <div className="mt-8 flex flex-wrap gap-3">
                {['NIST', 'ISO', 'CIS', 'SOC 2'].map((badge, index) => (
                  <span
                    key={badge}
                    className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 font-medium hover:border-cyan-500/50 hover:bg-slate-800/80 transition-all duration-300 cursor-default"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 rounded-2xl blur-2xl group-hover:from-cyan-500/20 group-hover:to-teal-500/20 transition-all duration-500" />
              <div className="relative bg-slate-900 border border-slate-800 rounded-2xl p-8 group-hover:border-cyan-500/30 transition-all duration-300">
                <Quote className="w-10 h-10 text-cyan-500/30 mb-4 group-hover:text-cyan-500/50 group-hover:scale-110 transition-all duration-300" />
                <p className="text-slate-300 text-lg italic leading-relaxed">
                  "We partner with organizations to identify what matters most, strengthen security posture, and create defensible, repeatable risk management processes."
                </p>
              </div>
            </div>
          </div>
        </div>
      </ParallaxSection>

      {/* Mission & Vision */}
      <ParallaxSection className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <Card className="bg-slate-900/50 border-slate-800 overflow-hidden group hover:border-cyan-500/50 transition-all duration-300 interactive-card">
              <CardContent className="p-8">
                <div className="relative w-14 h-14 mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/30 to-teal-600/30 rounded-xl blur-lg opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative w-full h-full bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">Mission Statement</h3>
                <p className="text-slate-400 leading-relaxed">
                  {aboutUs.mission}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="bg-slate-900/50 border-slate-800 overflow-hidden group hover:border-teal-500/50 transition-all duration-300 interactive-card">
              <CardContent className="p-8">
                <div className="relative w-14 h-14 mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500/30 to-cyan-600/30 rounded-xl blur-lg opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative w-full h-full bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Lightbulb className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors">Vision Statement</h3>
                <p className="text-slate-400 leading-relaxed">
                  {aboutUs.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </ParallaxSection>

      {/* Core Values */}
      <ParallaxSection className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium text-sm tracking-wider uppercase">Our Foundation</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-6">Core Values</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              These principles guide everything we do and how we work with our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = iconMap[value.icon];
              return (
                <Card
                  key={value.id}
                  className="bg-slate-900 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 group interactive-card"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="relative w-12 h-12 flex-shrink-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300" />
                        <div className="relative w-full h-full bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          {IconComponent && <IconComponent className="w-6 h-6 text-cyan-400 group-hover:text-teal-400 transition-colors duration-300" />}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                          {value.title}
                        </h3>
                        <p className="text-slate-400 text-sm">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </ParallaxSection>
    </div>
  );
};

export default AboutPage;
